(() => {
  const initSalePage = (root) => {
    if (!root || root.dataset.saleReady === "true") return;
    root.dataset.saleReady = "true";

    const buttons = [...root.querySelectorAll("[data-sale-tab]")];
    const panels = [...root.querySelectorAll("[data-sale-panel]")];
    const viewAll = root.querySelector("[data-sale-view-all]");

    const updateRail = (panel) => {
      const track = panel?.querySelector("[data-sale-track]");
      const previous = panel?.querySelector("[data-sale-rail-prev]");
      const next = panel?.querySelector("[data-sale-rail-next]");
      if (!track || !previous || !next) return;

      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maximum > 2;
      previous.hidden = !hasOverflow;
      next.hidden = !hasOverflow;
      previous.disabled = !hasOverflow || track.scrollLeft <= 2;
      next.disabled = !hasOverflow || track.scrollLeft >= maximum - 2;
    };

    const scrollRail = (panel, direction) => {
      const track = panel.querySelector("[data-sale-track]");
      if (!track) return;
      const firstCard = track.querySelector("[data-product-card]");
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: distance * direction, behavior: "smooth" });
    };

    panels.forEach((panel) => {
      const track = panel.querySelector("[data-sale-track]");
      const previous = panel.querySelector("[data-sale-rail-prev]");
      const next = panel.querySelector("[data-sale-rail-next]");
      if (!track || !previous || !next) return;

      previous.addEventListener("click", () => scrollRail(panel, -1));
      next.addEventListener("click", () => scrollRail(panel, 1));
      track.addEventListener("scroll", () => updateRail(panel), { passive: true });
      if ("ResizeObserver" in window) new ResizeObserver(() => updateRail(panel)).observe(track);
    });

    const activateTab = (targetId, moveFocus = false) => {
      const activePanel = panels.find((panel) => panel.id === targetId);
      if (!activePanel) return;

      buttons.forEach((button) => {
        const active = button.dataset.saleTab === targetId;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-selected", String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && moveFocus) button.focus();
      });

      panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      if (viewAll && activePanel.dataset.saleViewAllUrl) {
        viewAll.href = activePanel.dataset.saleViewAllUrl;
        viewAll.setAttribute("aria-label", activePanel.dataset.saleViewAllLabel || "View all products");
      }

      window.requestAnimationFrame(() => {
        updateRail(activePanel);
        window.dispatchEvent(new Event("resize"));
      });
    };

    buttons.forEach((button, index) => {
      button.addEventListener("click", () => activateTab(button.dataset.saleTab));
      button.addEventListener("keydown", (event) => {
        let nextIndex = index;
        if (event.key === "ArrowRight") nextIndex = (index + 1) % buttons.length;
        else if (event.key === "ArrowLeft") nextIndex = (index - 1 + buttons.length) % buttons.length;
        else if (event.key === "Home") nextIndex = 0;
        else if (event.key === "End") nextIndex = buttons.length - 1;
        else return;
        event.preventDefault();
        activateTab(buttons[nextIndex].dataset.saleTab, true);
      });
    });

    const initialPanel = panels.find((panel) => !panel.hidden) || panels[0];
    if (initialPanel) window.requestAnimationFrame(() => updateRail(initialPanel));

    const lookbookTrack = root.querySelector("[data-lookbook-track]");
    const lookbookPrevious = root.querySelector("[data-lookbook-prev]");
    const lookbookNext = root.querySelector("[data-lookbook-next]");

    if (lookbookTrack && lookbookPrevious && lookbookNext) {
      const updateLookbookArrows = () => {
        const maximum = Math.max(0, lookbookTrack.scrollWidth - lookbookTrack.clientWidth);
        lookbookPrevious.disabled = lookbookTrack.scrollLeft <= 2;
        lookbookNext.disabled = lookbookTrack.scrollLeft >= maximum - 2;
      };

      const scrollLookbook = (direction) => {
        const firstCard = lookbookTrack.querySelector(".bs-offers-page__lookbook-card");
        const styles = window.getComputedStyle(lookbookTrack);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : lookbookTrack.clientWidth * 0.8;
        lookbookTrack.scrollBy({ left: distance * direction, behavior: "smooth" });
      };

      lookbookPrevious.addEventListener("click", () => scrollLookbook(-1));
      lookbookNext.addEventListener("click", () => scrollLookbook(1));
      lookbookTrack.addEventListener("scroll", updateLookbookArrows, { passive: true });
      window.addEventListener("resize", updateLookbookArrows, { passive: true });

      // The arrows were measured once, on the frame after init, while the
      // lookbook was still below the fold with its images unsized. The track
      // was then no wider than its box, so "next" disabled itself and nothing
      // re-ran this: only a window resize did, which is why the arrows looked
      // dead until you happened to resize. Watching the track re-checks them
      // the moment the images give it a real width. The product rail above
      // already does exactly this.
      if ("ResizeObserver" in window) {
        new ResizeObserver(updateLookbookArrows).observe(lookbookTrack);
      }
      window.requestAnimationFrame(updateLookbookArrows);
    }

    const clock = root.querySelector("[data-sale-clock]");
    if (!clock) return;

    const daysNode = clock.querySelector("[data-sale-days]");
    const hoursNode = clock.querySelector("[data-sale-hours]");
    const minutesNode = clock.querySelector("[data-sale-minutes]");
    const secondsNode = clock.querySelector("[data-sale-seconds]");
    const expiredNode = root.querySelector("[data-sale-expired]");
    const target = Date.parse(root.dataset.countdownEnd || "");

    if (!Number.isFinite(target)) {
      clock.hidden = true;
      if (expiredNode) expiredNode.hidden = false;
      return;
    }

    const renderClock = () => {
      const remaining = Math.max(0, target - Date.now());
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      if (daysNode) daysNode.textContent = String(days).padStart(2, "0");
      if (hoursNode) hoursNode.textContent = String(hours).padStart(2, "0");
      if (minutesNode) minutesNode.textContent = String(minutes).padStart(2, "0");
      if (secondsNode) secondsNode.textContent = String(seconds).padStart(2, "0");

      if (remaining === 0) {
        clock.hidden = true;
        if (expiredNode) expiredNode.hidden = false;
        if (root.dataset.saleTimer) window.clearInterval(Number(root.dataset.saleTimer));
      }
    };

    renderClock();
    root.dataset.saleTimer = String(window.setInterval(renderClock, 1000));
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-offers-sale]").forEach(initSalePage);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-offers-sale][data-sale-timer]").forEach((root) => {
      window.clearInterval(Number(root.dataset.saleTimer));
    });
  });
})();
